import type { ReactNode } from 'react';
import { motion } from 'motion/react';

interface AnimatedModalProps {
  children: ReactNode;
  /** Optional non-scrolling footer pinned below the scrolling body. */
  footer?: ReactNode;
  onClose: () => void;
  /** Max width of the dialog panel. Default 480px. */
  maxWidthClassName?: string;
}

/**
 * Pop-in / pop-out modal shell (Motion).
 * Parent must wrap with <AnimatePresence> so exit anim plays before unmount.
 */
export function AnimatedModal({
  children,
  footer,
  onClose,
  maxWidthClassName = 'max-w-[480px]',
}: AnimatedModalProps) {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      <motion.div
        className="absolute inset-0 bg-text/40"
        onClick={onClose}
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      <motion.div
        className={`relative flex max-h-[90vh] w-full ${maxWidthClassName} flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg`}
        initial={{ opacity: 0, scale: 0.92, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 10 }}
        transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.8 }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-border bg-surface px-6 py-3">{footer}</div>
        )}
      </motion.div>
    </motion.div>
  );
}
